<template>
    <div class="">
        <!-- DataTable Component -->
        <DataTable
            :perPage="itemsPerPage"
            :columns="visibleColumns"
            :items="tableData"
            :currentPage="currentPage"
            :totalPages="totalPages"
            :enableDragDrop="false"
            :enableMultiSelect="false"
            rowClass="text-center"
            @page-changed="handlePageChange"
        >
            <!-- Toolbar Slot -->
            <template #toolbar>
                <div class="ppv2-flex ppv2-flex-wrap ppv2-items-center" :style="{ gap: normalizeSize(16, true) }">
                    <!-- Create Button -->
                    <a
                        href="/back-office/customer/create"
                        class="ppv2-min-h-[48px] ppv2-flex ppv2-items-center !ppv2-text-white hover:ppv2-text-white ppv2-gap-3 ppv2-pl-4 ppv2-pr-[20px] ppv2-py-3 ppv2-rounded-xl ppv2-bg-[#7D2DBD] ppv2-text-white hover:ppv2-bg-[#6b26a3] ppv2-transition-all ppv2-shadow-lg"
                    >
                        <Plus :size="20" />
                        <span class="poppinsRegular" :style="{ fontSize: normalizeFontSize(14) }">
                            {{ $t('customer.add_customer') }}
                        </span>
                    </a>

                    <!-- Column Visibility Toggle -->
                    <div class="ppv2-relative" ref="columnsDropdown">
                        <button
                            @click="showColumns = !showColumns"
                            class="ppv2-flex ppv2-items-center ppv2-gap-3 ppv2-px-4 ppv2-py-3 ppv2-rounded-xl ppv2-border ppv2-border-solid ppv2-border-black ppv2-text-black hover:ppv2-bg-opacity-80 ppv2-transition-all"
                            :disabled="selectedCampagneUuid !== ''"
                        >
                            <LayoutGrid class="ppv2-w-5 ppv2-h-5" />
                            <span class="poppinsRegular" :style="{ fontSize: normalizeFontSize(16) }">
                                {{ $t('customer.columns') }}
                            </span>
                            <ChevronDown class="ppv2-w-4 ppv2-h-4" />
                        </button>
                        <!-- Dropdown -->
                        <div
                            v-if="showColumns"
                            class="menuDropdown ppv2-absolute ppv2-top-full ppv2-left-0 ppv2-rounded-md ppv2-overflow-hidden"
                        >
                            <div class="ppv2-max-h-[300px] ppv2-overflow-y-auto">
                                <label
                                    v-for="(column, index) in allColumns"
                                    :key="index"
                                    class="dropdownItem ppv2-flex ppv2-items-center ppv2-gap-3 ppv2-px-4 ppv2-py-1 ppv2-cursor-pointer ppv2-transition-colors"
                                >
                                    <BaseCheckbox
                                        :value="selectedColumnKeys.includes(column.key)"
                                        @input="toggleColumn(column.key)"
                                        size="sm"
                                        :hasBorderChecked="true"
                                    />
                                    <span class="poppinsLight" :style="{ fontSize: normalizeFontSize(14) }">{{
                                        column.label
                                    }}</span>
                                </label>
                            </div>
                        </div>
                    </div>

                    <!-- Export Button -->
                    <button
                        v-if="selectedCampagneUuid == ''"
                        @click="downloadCustomers"
                        class="ppv2-flex ppv2-items-center ppv2-gap-3 ppv2-px-4 ppv2-py-3 ppv2-rounded-xl ppv2-border ppv2-border-solid ppv2-border-black ppv2-text-black hover:ppv2-bg-opacity-80 ppv2-transition-all"
                        :disabled="exportLoading"
                    >
                        <svg
                            v-if="exportLoading"
                            class="ppv2-w-5 ppv2-h-5 ppv2-animate-spin"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                        >
                            <circle
                                class="ppv2-opacity-25"
                                cx="12"
                                cy="12"
                                r="10"
                                stroke="currentColor"
                                stroke-width="4"
                            ></circle>
                            <path
                                class="ppv2-opacity-75"
                                fill="currentColor"
                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            ></path>
                        </svg>
                        <svg v-else width="30" height="30" viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path
                                d="M1.21289 17.6012C1.21289 14.7752 2.69884 12.2962 4.93205 10.9029C5.53943 6.12426 9.62017 2.42969 14.5638 2.42969C19.5074 2.42969 23.5882 6.12426 24.1956 10.9029C26.4288 12.2962 27.9148 14.7752 27.9148 17.6012C27.9148 21.7541 24.7059 25.1575 20.6324 25.4675L8.49522 25.4904C4.42171 25.1575 1.21289 21.7541 1.21289 17.6012ZM20.4483 23.0469C23.2804 22.8315 25.4873 20.4609 25.4873 17.6012C25.4873 15.692 24.5023 13.9554 22.9107 12.9625L21.9328 12.3524L21.7875 11.209C21.3285 7.59775 18.24 4.85713 14.5638 4.85713C10.8877 4.85713 7.79912 7.59775 7.34012 11.209L7.19479 12.3524L6.21692 12.9625C4.62534 13.9554 3.64034 15.692 3.64034 17.6012C3.64034 20.4609 5.84728 22.8315 8.67935 23.0469L8.88968 23.063H20.238L20.4483 23.0469ZM15.7776 14.5669H19.4187L14.5638 20.6355L9.70895 14.5669H13.3501V9.71202H15.7776V14.5669Z"
                                fill="currentColor"
                            />
                        </svg>
                        <span class="poppinsRegular" :style="{ fontSize: normalizeFontSize(16) }">
                            {{ exportLoading ? $t('customer.export_loading') : $t('customer.exporter') }}
                        </span>
                    </button>

                    <div class="ppv2-flex-1"></div>

                    <!-- Search Input -->
                    <div class="ppv2-relative">
                        <Search v-model="search" :emitGlobal="false" @search-updated="handleSearchUpdate" />
                    </div>

                    <!-- Merge Button -->
                    <a
                        v-if="selectedCampagneUuid == ''"
                        href="/back-office/customer/merge"
                        class="ppv2-flex ppv2-items-center ppv2-gap-3 ppv2-px-4 ppv2-py-3 ppv2-rounded-xl ppv2-border ppv2-border-solid ppv2-border-black ppv2-text-black hover:ppv2-bg-opacity-80 ppv2-transition-all"
                    >
                        <GitMerge class="ppv2-w-5 ppv2-h-5" />
                        <span class="poppinsRegular" :style="{ fontSize: normalizeFontSize(16) }">
                            {{ $t('customer.fusionner') }}
                        </span>
                    </a>
                </div>
            </template>

            <!-- Column: First Name — show "Client de passage" when walk-in -->
            <template #column-first_name="{ item }">
                <span
                    v-if="isWalkInCustomer(item)"
                    class="ppv2-text-black ppv2-text-opacity-70"
                    :style="{ fontSize: normalizeFontSize(13) }"
                    >{{ $t('invoices.walk_in_customer') }}</span
                >
                <span v-else>{{ item.first_name }}</span>
            </template>

            <!-- Column: Last Name — hide when walk-in (label already in first_name column) -->
            <template #column-last_name="{ item }">
                <span v-if="!isWalkInCustomer(item)">{{ item.last_name }}</span>
            </template>

            <!-- Column: Origin -->
            <template #column-origin="{ item }">
                <span v-html="item.origin"></span>
            </template>

            <!-- Column: Actions -->
            <template #column-actions="{ item }">
                <div class="ppv2-flex ppv2-items-center ppv2-justify-center ppv2-gap-2">
                    <!-- Définir mot de passe -->
                    <a
                        :href="`/back-office/customer/${item.id}/set_password`"
                        class="ppv2-p-1 ppv2-text-black hover:ppv2-text-black hover:ppv2-bg-black hover:ppv2-bg-opacity-10 ppv2-rounded ppv2-transition-all"
                        :title="$t('customer.set_password')"
                    >
                        <KeyRound :size="18" />
                    </a>

                    <!-- Caisse -->
                    <form action="/back-office/caisse/customer/change" method="GET" class="ppv2-inline">
                        <input type="hidden" id="customerSelect" name="customerSelect" :value="item.id" />
                        <button
                            type="submit"
                            class="ppv2-p-1 ppv2-text-black hover:ppv2-text-black hover:ppv2-bg-black hover:ppv2-bg-opacity-10 ppv2-rounded ppv2-transition-all"
                            :title="$t('customer.caisse')"
                        >
                            <Computer :size="18" />
                        </button>
                    </form>

                    <!-- Modifier -->
                    <a
                        :href="`/back-office/customer/${item.id}/edit`"
                        class="ppv2-p-1 ppv2-text-black hover:ppv2-text-black hover:ppv2-bg-black hover:ppv2-bg-opacity-10 ppv2-rounded ppv2-transition-all"
                        :title="$t('customer.edit')"
                    >
                        <SquarePen :size="18" />
                    </a>

                    <!-- Désactiver/Réactiver -->
                    <button
                        v-if="item.status_id == 1"
                        @click="handleDisable(item.id)"
                        class="ppv2-p-1 ppv2-text-black hover:ppv2-text-black hover:ppv2-bg-black hover:ppv2-bg-opacity-10 ppv2-rounded ppv2-transition-all"
                        :title="$t('customer.deactivate_client_tooltip')"
                    >
                        <UserX :size="18" />
                    </button>
                    <button
                        v-else
                        @click="handleEnable(item.id)"
                        class="ppv2-p-1 ppv2-text-black hover:ppv2-text-black hover:ppv2-bg-black hover:ppv2-bg-opacity-10 ppv2-rounded ppv2-transition-all"
                        :title="$t('customer.reactivate_client_tooltip')"
                    >
                        <UserCheck :size="18" />
                    </button>

                    <!-- Planifier réservation -->
                    <a
                        :href="`/back-office/planning?modaladd=1&fillclient=${item.id}`"
                        class="ppv2-p-1 ppv2-text-black hover:ppv2-text-black hover:ppv2-bg-black hover:ppv2-bg-opacity-10 ppv2-rounded ppv2-transition-all"
                        :title="$t('customer.plan_reservation')"
                    >
                        <CalendarPlus :size="18" />
                    </a>
                </div>
            </template>
        </DataTable>
    </div>
</template>

<script>
import {
    KeyRound,
    Computer,
    SquarePen,
    UserX,
    UserCheck,
    CalendarPlus,
    Trash2,
    Plus,
    LayoutGrid,
    ChevronDown,
    CloudDownload,
    GitMerge,
    X,
} from 'lucide-vue'
import { eventBus } from './eventBus'
import ApiManager from './Api/ApiManagerBoCutomer.js'
import DataTable from '../Shared/DataTable.vue'
import Search from '../V3/Experience/components/Search.vue'
import BaseCheckbox from '../Shared/BaseCheckbox.vue'
import normalizeSize from '../../utils/normalizeSize'
import normalizeFontSize from '../../utils/normalizeFontSize'
import { getPerPage, setPerPage } from '../../utils/perPage'

export default {
    name: 'CustomerDatatable',
    components: {
        KeyRound,
        Computer,
        SquarePen,
        UserX,
        UserCheck,
        CalendarPlus,
        Trash2,
        Plus,
        LayoutGrid,
        ChevronDown,
        CloudDownload,
        GitMerge,
        X,

        DataTable,
        Search,
        BaseCheckbox,
    },
    props: {
        selectedCampagneUuid: {
            type: String,
            default: () => '',
        },
        customersEmailsStatus: {
            type: String,
            default: () => '',
        },
    },
    data() {
        return {
            search: '',
            tableData: [],
            itemsPerPage: getPerPage('customers_legacy_items_per_page', 10),
            recordsTotal: 0,
            currentPage: 1,
            loading: false,
            exportLoading: false,
            applyFilters: [],
            showColumns: false,
            // Initially selected columns (keys)
            selectedColumnKeys: ['id', 'first_name', 'last_name', 'email', 'phone_number', 'created_at', 'actions'],
        }
    },
    computed: {
        // Define all available columns
        allColumns() {
            return [
                { key: 'id', label: 'Id', sortable: true, type: 'number' },
                { key: 'first_name', label: this.$t('customer.first_name'), sortable: true, type: 'text' },
                { key: 'last_name', label: this.$t('customer.last_name'), sortable: true, type: 'text' },
                { key: 'email', label: this.$t('customer.email'), sortable: true, type: 'text' },
                { key: 'phone_number', label: this.$t('customer.phone_number'), sortable: true, type: 'text' },
                { key: 'birthday', label: this.$t('customer.birthday'), sortable: false, type: 'date' },
                { key: 'addresses', label: this.$t('customer.addresses'), sortable: false, type: 'text' },
                { key: 'age', label: this.$t('customer.age'), sortable: false, type: 'number' },
                { key: 'gender', label: this.$t('customer.gender'), sortable: false, type: 'text' },
                { key: 'origin', label: this.$t('customer.origin'), sortable: false, type: 'text' },
                {
                    key: 'spent',
                    label: this.$t('customer.spent', { symbol: this.$symbolCurrency }),
                    sortable: false,
                    type: 'number',
                },
                { key: 'loyalty_points', label: this.$t('customer.loyalty_points'), sortable: false, type: 'number' },
                { key: 'first_coming', label: this.$t('customer.first_coming'), sortable: false, type: 'date' },
                { key: 'last_coming', label: this.$t('customer.last_coming'), sortable: false, type: 'date' },
                { key: 'created_at', label: this.$t('customer.created_at'), sortable: true, type: 'date' },
                { key: 'actions', label: this.$t('customer.actions'), sortable: false, type: 'actions' },
            ]
        },
        totalPages() {
            return Math.ceil(this.recordsTotal / this.itemsPerPage)
        },
        visibleColumns() {
            return this.allColumns.filter((column) => this.selectedColumnKeys.includes(column.key))
        },
    },
    methods: {
        normalizeFontSize,
        normalizeSize,
        isWalkInCustomer(item) {
            if (!item) return true
            const fn = (item.first_name || '').trim()
            const ln = (item.last_name || '').trim()
            return (!fn || fn === '-') && (!ln || ln === '-')
        },
        toggleColumn(key) {
            if (this.selectedColumnKeys.includes(key)) {
                this.selectedColumnKeys = this.selectedColumnKeys.filter((k) => k !== key)
            } else {
                this.selectedColumnKeys.push(key)
            }
            const params = this.getParams()
            this.fetchData(params)
        },
        handlePageChange(page) {
            this.currentPage = page
            const params = this.getParams()
            this.fetchData(params)
        },
        handlePerPageChange(perPage) {
            this.itemsPerPage = Number(perPage)
            setPerPage('customers_legacy_items_per_page', this.itemsPerPage)
            const params = this.getParams()
            this.fetchData(params)
        },
        handleSearchUpdate(searchValue) {
            this.search = searchValue
            this.currentPage = 1
            const params = this.getParams()
            this.fetchData(params)
        },
        handleDownloadCustomersEmitEvent() {
            this.downloadCustomers()
        },
        handleClickOutside(event) {
            if (this.$refs.columnsDropdown && !this.$refs.columnsDropdown.contains(event.target)) {
                this.showColumns = false
            }
        },

        getParams() {
            return {
                ajax: true,
                search: this.search,
                page: this.currentPage,
                perPage: this.itemsPerPage,
                columns: this.visibleColumns.map((c) => c.key), // Send keys as columns
                selectedCampagneUuid: this.selectedCampagneUuid,
                customersEmailsStatus: this.customersEmailsStatus,
            }
        },

        async fetchData(params) {
            this.loading = true
            try {
                const _result = await ApiManager.fetchCustomers(params)
                const _customers = _result?.data ?? []

                this.recordsTotal = _result.total
                // Map data but keep all properties for flexibility
                this.tableData = _customers.map((item) => {
                    return {
                        ...item,
                        // Ensure keys match what DataTable expects if they differ from API
                    }
                })
            } catch (error) {
                this.$toast.error(error?.response?.data?.message ?? this.$t('customer.error_occurred'))
            } finally {
                this.loading = false
            }
        },

        async downloadCustomers() {
            this.exportLoading = true
            try {
                await ApiManager.downloadCustomers(this.getParams())
            } catch (error) {
                this.$toast.error(error.response.data.message)
            } finally {
                this.exportLoading = false
            }
        },

        handleDisable(id) {
            this.$modal.show('dialog', {
                title: 'Alert',
                text: `<p class="mb-0 p-3">${this.$t('customer.deactivate_confirm')}</p>`,
                buttons: [
                    {
                        title: `<b class="btn btn-dark width-btn height_btn_page rounded-12">${this.$t(
                            'customer.deactivate'
                        )}</b>`,
                        handler: () => {
                            this.disable(id)
                        },
                    },
                    {
                        title: `<b class="btn btn-outline-dark width-btn height_btn_page rounded-12">${this.$t(
                            'customer.close'
                        )}</b>`,
                        handler: () => {
                            this.$modal.hide('dialog')
                        },
                    },
                ],
            })
        },

        async disable(id) {
            this.loading = true
            try {
                const res = await ApiManager.disableCustomerById(id)
                if (res?.status === 200) {
                    this.$toast.success(this.$t('customer.deactivated_success'))
                }
            } catch (error) {
                this.$toast.error(error.response.data.message)
            } finally {
                this.loading = false
                const params = this.getParams()
                this.fetchData(params)
                this.$modal.hide('dialog')
            }
        },

        handleEnable(id) {
            this.$modal.show('dialog', {
                title: 'Alert',
                text: this.$t('customer.reactivate_confirm'),
                buttons: [
                    {
                        title: this.$t('customer.activate'),
                        handler: () => {
                            this.enable(id)
                        },
                    },
                    {
                        title: `<b>${this.$t('customer.close')}</b>`,
                        handler: () => {
                            this.$modal.hide('dialog')
                        },
                    },
                ],
            })
        },

        async enable(id) {
            this.loading = true
            try {
                const res = await ApiManager.enableCustomerById(id)
                if (res?.status === 200) {
                    this.$toast.success(this.$t('customer.activated_success'))
                }
            } catch (error) {
                this.$toast.error(error.response.data.message)
            } finally {
                this.loading = false
                const params = this.getParams()
                this.fetchData(params)
                this.$modal.hide('dialog')
            }
        },

        async fetchApplyFilters() {
            try {
                const result = await ApiManager.fetchApplyFilters()
                this.applyFilters = result?.userCustomFilter ?? []
            } catch (error) {
                this.$toast.error(error.response.data.message)
            }
        },
    },
    mounted() {
        const params = this.getParams()
        this.fetchData(params)
        this.fetchApplyFilters()
        eventBus.$on('dt-selected-perpage-changed', this.handlePerPageChange)
        document.addEventListener('click', this.handleClickOutside)
    },
    beforeDestroy() {
        eventBus.$off('dt-selected-perpage-changed', this.handlePerPageChange)
        document.removeEventListener('click', this.handleClickOutside)
    },
}
</script>

<style scoped lang="scss">
.loader-container {
    position: relative;
    width: 100%;
    height: 100%;
}

.loader {
    position: fixed;
    top: 40%;
    left: 50%;
    transform: translate(-50%, -50%);
    border: 4px solid rgba(0, 0, 0, 0.1);
    border-left-color: #333;
    border-radius: 50%;
    width: 30px;
    height: 30px;
    animation: spin 1s linear infinite;
}

@keyframes spin {
    0% {
        transform: rotate(0deg);
    }

    100% {
        transform: rotate(360deg);
    }
}

.vue-dialog {
    font-size: clamp(12px, 0.729545vw, 16px);
    width: 100% !important;
    padding-bottom: 35px;
    text-align: center;
    max-width: 600px;
    left: 0 !important;
    margin: auto;
}

.vue-dialog-button b {
    line-height: 44px;
}

.vue-dialog-buttons {
    border-top: none !important;
    gap: 20px;
}

.vue-dialog-button:not(:first-of-type) {
    border-left: none !important;
}

.vue-dialog-content-title {
    min-height: 1.75rem;
    font-size: 1.125rem;
    font-weight: 500;
    line-height: 1.75rem;
    border-bottom: 1px solid #eee;
}

.vue-dialog-content {
    padding: 14px 0;
}

@media (max-width: 767.98px) {
    .vue-dialog-button b {
        line-height: 30px;
    }
}
</style>
